// Съдържанието на попъпа на филтър в колона: една или няколко секции —
// списък с отметки (броячите са фасетни: колко реда остават при останалите
// филтри), по желание търсачка, „Само това" при посочване/фокус на опция,
// „Всички" / „Изчисти"; или диапазон от–до за пари. Изборът се прилага веднага
// — няма „Приложи".
//
// Клавиатура: ↑/↓ (и Home/End) по търсачките и опциите, Space отмята, Enter
// върху опция = „Само това" и затваря, Enter в търсачката = само първото
// съвпадение, Esc първо чисти текста в търсачката, после затваря и връща
// фокуса на фунията. Всеки Esc е с preventDefault (общото правило за Esc).
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { useInventoryShell } from "../shell/InventoryShellContext";
import { KbdChip } from "../KbdChip";
import { CheckBox } from "./CheckBox";
import { filterOptionsByQuery, parseRangeInput, type FacetOption } from "./columnFilterModel";
import {
  isSearchable,
  type ColumnFilterSection,
  type ListFilterSection,
  type RangeFilterSection,
} from "./useColumnFilters";

// Затваряне: `returnFocus` — от клавиатурата (фокусът се връща на фунията)
type CloseHandler = (returnFocus: boolean) => void;

const SECTION_TITLE_CLASS =
  "min-w-0 flex-1 truncate text-[11px] font-semibold uppercase leading-[normal] tracking-[0.1em] text-muted";
const SECTION_LINK_CLASS =
  "cursor-pointer border-none bg-transparent p-0 text-xs leading-[normal] text-body underline outline-none hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--color-brand-tint)] disabled:cursor-default disabled:text-ghost disabled:no-underline";

const inputClass = (isNarrow: boolean) =>
  `w-full min-w-0 rounded-lg border border-shade-3 bg-surface px-2.5 py-1.5 text-ink outline-none placeholder:text-faint focus:border-brand ${
    isNarrow ? "text-base" : "text-[13px]"
  }`;

// ─── Секция-списък ────────────────────────────────────────────────────────
const ListSection = ({ section, onClose }: { section: ListFilterSection; onClose: CloseHandler }) => {
  const { t } = useTranslation();
  const { isNarrow } = useInventoryShell();
  const [query, setQuery] = useState("");
  const options = section.getOptions();
  const searchable = isSearchable(section, options.length);
  const visible = searchable ? filterOptionsByQuery(options, query) : options;
  const selected = section.selected;

  const toggle = (value: string) =>
    section.onChange(
      selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value],
    );
  const onlyThis = (value: string) => {
    section.onChange([value]);
    onClose(true);
  };
  // „Всички" отмята видимите (при търсене — само намерените) към избраното
  const selectVisible = () =>
    section.onChange(Array.from(new Set([...selected, ...visible.map((option) => option.value)])));

  const onOptionKeyDown = (event: KeyboardEvent<HTMLButtonElement>, option: FacetOption) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    event.stopPropagation();
    onlyThis(option.value);
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2.5 px-2 pb-1.5 pt-2.5">
        <span className={SECTION_TITLE_CLASS}>{section.title}</span>
        <button type="button" onClick={selectVisible} className={SECTION_LINK_CLASS}>
          {t("inventoryPage.cfSelectAll")}
        </button>
        <button
          type="button"
          onClick={() => section.onChange([])}
          disabled={!selected.length}
          className={SECTION_LINK_CLASS}
        >
          {t("inventoryPage.filterClear")}
        </button>
      </div>
      {searchable && (
        <div className="px-1.5 pb-1.5">
          <input
            data-nav
            data-search
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("inventoryPage.filterSearch")}
            aria-label={`${section.title} — ${t("inventoryPage.filterSearch")}`}
            onKeyDown={(event) => {
              if (event.key === "Escape" && query) {
                event.preventDefault();
                event.stopPropagation();
                setQuery("");
              } else if (event.key === "Enter" && visible.length) {
                event.preventDefault();
                event.stopPropagation();
                onlyThis(visible[0].value);
              }
            }}
            className={inputClass(isNarrow)}
          />
        </div>
      )}
      <div role="group" aria-label={section.title} className="max-h-[260px] overflow-auto">
        {visible.map((option) => {
          const checked = selected.includes(option.value);
          return (
            <div
              key={option.value}
              className={`group flex items-center gap-1.5 rounded-[7px] pr-2 hover:bg-shade-1 ${
                checked ? "bg-brand-tint" : ""
              }`}
            >
              <button
                type="button"
                data-option
                data-nav
                role="checkbox"
                aria-checked={checked}
                onClick={() => toggle(option.value)}
                onKeyDown={(event) => onOptionKeyDown(event, option)}
                className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 rounded-[7px] border-none bg-transparent px-2 py-[7px] text-left text-[13px] leading-[normal] outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--color-brand)]"
              >
                <CheckBox checked={checked} />
                <span
                  className={`min-w-0 flex-1 truncate ${
                    option.count || checked ? "text-ink" : "text-faint"
                  }`}
                >
                  {option.label}
                </span>
              </button>
              <button
                type="button"
                tabIndex={-1}
                onClick={() => onlyThis(option.value)}
                title="Enter"
                className="hidden shrink-0 cursor-pointer border-none bg-transparent p-0 text-[11.5px] leading-[normal] text-brand-deep-ink underline group-focus-within:inline group-hover:inline"
              >
                {t("inventoryPage.cfOnly")}
              </button>
              <span className="min-w-[24px] shrink-0 text-right font-sheet text-xs text-muted">
                {option.count}
              </span>
            </div>
          );
        })}
        {visible.length === 0 && (
          <div className="px-2 py-2.5 text-[13px] text-muted">{t("inventoryPage.filterNoMatch")}</div>
        )}
      </div>
    </div>
  );
};

// ─── Секция-диапазон (от–до) ──────────────────────────────────────────────
const plainNumber = (value: number | null) => (value == null ? "" : String(value));

const RangeSection = ({ section, onClose }: { section: RangeFilterSection; onClose: CloseHandler }) => {
  const { t } = useTranslation();
  const { isNarrow } = useInventoryShell();
  const { value } = section;
  // Текстът в полетата: пазим написаното („1,"), докато съвпада със
  // стойността; сменена отвън (Изчисти, ✕ в лентата) — показваме нея
  const [draft, setDraft] = useState({ min: plainNumber(value.min), max: plainNumber(value.max) });
  const minText = parseRangeInput(draft.min) === value.min ? draft.min : plainNumber(value.min);
  const maxText = parseRangeInput(draft.max) === value.max ? draft.max : plainNumber(value.max);
  const bounds = section.getBounds();
  const active = value.min != null || value.max != null;

  const change = (edge: "min" | "max", text: string) => {
    setDraft((current) => ({ ...current, [edge]: text }));
    section.onChange({ ...value, [edge]: parseRangeInput(text) });
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    event.stopPropagation();
    onClose(true);
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2.5 px-2 pb-1.5 pt-2.5">
        <span className={SECTION_TITLE_CLASS}>{section.title}</span>
        <button
          type="button"
          onClick={() => section.onChange({ min: null, max: null })}
          disabled={!active}
          className={SECTION_LINK_CLASS}
        >
          {t("inventoryPage.filterClear")}
        </button>
      </div>
      <div className="flex items-center gap-2 px-1.5 pb-2">
        <input
          inputMode="decimal"
          value={minText}
          onChange={(event) => change("min", event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={bounds ? `${t("inventoryPage.cfFrom")} ${section.format(bounds[0])}` : t("inventoryPage.cfFrom")}
          aria-label={`${section.title} — ${t("inventoryPage.cfFrom")}`}
          className={`${inputClass(isNarrow)} font-sheet`}
        />
        <span className="text-faint">–</span>
        <input
          inputMode="decimal"
          value={maxText}
          onChange={(event) => change("max", event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={bounds ? `${t("inventoryPage.cfTo")} ${section.format(bounds[1])}` : t("inventoryPage.cfTo")}
          aria-label={`${section.title} — ${t("inventoryPage.cfTo")}`}
          className={`${inputClass(isNarrow)} font-sheet`}
        />
      </div>
    </div>
  );
};

// ─── Панелът ──────────────────────────────────────────────────────────────
type Props = {
  sections: ColumnFilterSection[];
  onClose: CloseHandler;
};

export const ColumnFilterPanel = ({ sections, onClose }: Props) => {
  const { t } = useTranslation();
  const { isNarrow } = useInventoryShell();
  const rootRef = useRef<HTMLDivElement | null>(null);

  // При отваряне фокусът отива в първото поле (търсачка, „от") или на първата
  // опция. Порталът на antd закача попъпа към документа СЛЕД ефекта на
  // съдържанието (измерено: isConnected=false) — затова опитът се повтаря,
  // докато попъпът стане част от страницата. preventScroll — попъпът още се
  // позиционира и страницата не бива да скача.
  useEffect(() => {
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const focusFirst = () => {
      const root = rootRef.current;
      if (!root) return;
      if (!root.isConnected) {
        if (attempts++ < 20) timer = setTimeout(focusFirst, 16);
        return;
      }
      if (root.contains(document.activeElement)) return;
      root.querySelector<HTMLElement>("input, button[data-option]")?.focus({ preventScroll: true });
    };
    focusFirst();
    return () => clearTimeout(timer);
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onClose(true);
      return;
    }
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    const target = event.target as HTMLElement;
    // Home/End в търсачката си местят курсора
    if (target.tagName === "INPUT" && (event.key === "Home" || event.key === "End")) return;
    const items = Array.from(rootRef.current?.querySelectorAll<HTMLElement>("[data-nav]") ?? []);
    const index = items.indexOf(target);
    if (index < 0) return;
    const options = items.filter((item) => item.hasAttribute("data-option"));
    const next =
      event.key === "Home"
        ? options[0]
        : event.key === "End"
          ? options[options.length - 1]
          : items[event.key === "ArrowDown" ? index + 1 : index - 1];
    event.preventDefault();
    event.stopPropagation();
    next?.focus();
  };

  const hasList = sections.some((section) => section.kind === "list");
  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label={sections.map((section) => section.title).join(" · ")}
      onKeyDown={onKeyDown}
      className={`flex max-h-[min(560px,75vh)] flex-col overflow-auto px-1.5 pb-1.5 ${
        isNarrow ? "w-[calc(100vw-32px)] max-w-[340px]" : "w-[290px]"
      }`}
    >
      {sections.map((section, index) => (
        <div key={section.key} className={index > 0 ? "border-t border-shade-2" : ""}>
          {section.kind === "list" ? (
            <ListSection section={section} onClose={onClose} />
          ) : (
            <RangeSection section={section} onClose={onClose} />
          )}
        </div>
      ))}
      {hasList && !isNarrow && (
        <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 border-t border-shade-2 px-2 pt-2 text-[11px] leading-[normal] text-muted">
          <span>
            <KbdChip>Space</KbdChip> {t("inventoryPage.cfKeyToggle")}
          </span>
          <span>
            <KbdChip>Enter</KbdChip> {t("inventoryPage.cfKeyOnly")}
          </span>
          <span>
            <KbdChip>Esc</KbdChip> {t("inventoryPage.catKeyEscClose")}
          </span>
        </div>
      )}
    </div>
  );
};

export default ColumnFilterPanel;
